import * as React from "react";
import { ClientsService, CustomFieldDefinition } from "gen/clients";
import Grid from "@mui/material/Grid";
import { SelectOneField } from "components/formikFields/SelectOneField/SelectOneField";
import { FieldName, FormValues } from "../CreateProjectSettings";
import { useQuery } from "react-query";
import { useFormikContext } from "formik";
import { FormFieldObserver } from "components/formikFields/FormFieldObserver/FormFieldObserver";
import { Box, LinearProgress } from "@mui/material";

interface Props {
  clientId: string;
}

const SubmissionModeConditions: React.FC<Props> = ({ clientId }) => {
  const { values, initialValues, setFieldValue } = useFormikContext<FormValues>();

  const { data: customFieldDefinitions, isLoading: isCustomFieldDefinitionssLoading } = useQuery(
    ["listCustomFieldDefinitions", clientId],
    {
      queryFn: () => ClientsService.listCustomFieldDefinitions({ clientId })
    }
  );

  const fieldNameOptions = React.useMemo(
    () =>
      (customFieldDefinitions || [])
        .filter(
          cfd =>
            (cfd.type === CustomFieldDefinition.type.LISTONLY || cfd.type === CustomFieldDefinition.type.LIST) &&
            cfd.options?.length
        )
        .map(cfd => ({ label: cfd.name, value: cfd.name })),
    [customFieldDefinitions]
  );

  const fieldNameValue = values[FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME];

  const fieldValueOptions = React.useMemo(() => {
    const selectedFieldName = fieldNameValue;
    const selectedFieldDefinition = customFieldDefinitions?.find(cfd => cfd.name === selectedFieldName);
    return selectedFieldDefinition?.options || [];
  }, [customFieldDefinitions, fieldNameValue]);

  // Cleanup effect: Reset field values for submission mode conditions when the component unmounts
  React.useEffect(() => {
    return () => {
      setFieldValue(FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME, "");
      setFieldValue(FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE, "");
    };
  }, [setFieldValue]);

  // Reset the field value for "Equals To" whenever the "When Field" changes
  const onSelectedFieldNameChange = React.useCallback(() => {
    if (fieldNameValue !== initialValues[FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME]) {
      setFieldValue(FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE, "");
    }
  }, [fieldNameValue, initialValues, setFieldValue]);

  if (isCustomFieldDefinitionssLoading) {
    return (
      <Box sx={{ width: "100%" }}>
        <LinearProgress />
      </Box>
    );
  }

  return (
    <Grid container={true} spacing={1}>
      <Grid item={true} xs={12} sm={6}>
        <SelectOneField
          name={FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME}
          label="When Field"
          options={fieldNameOptions}
          isLoading={isCustomFieldDefinitionssLoading}
          variant="outlined"
          required={true}
        />
        <FormFieldObserver
          fieldName={FieldName.SUBMISSION_MODE_CONDITION_FIELD_NAME}
          onChange={onSelectedFieldNameChange}
        />
      </Grid>
      <Grid item={true} xs={12} sm={6}>
        <SelectOneField
          label="Equals To"
          name={FieldName.SUBMISSION_MODE_CONDITION_FIELD_VALUE}
          options={fieldValueOptions}
          variant="outlined"
          required={true}
        />
      </Grid>
    </Grid>
  );
};

export { SubmissionModeConditions };
